import React, { useState, useEffect } from 'react';
import { Server, Activity, Clock, AlertTriangle, User, Building2 } from 'lucide-react';
import { instanceService } from '../services/instanceService';
import { authService } from '../services/authService';

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState({
    total: 0,
    running: 0,
    stopped: 0,
    pending: 0,
    other: 0
  });
  const [recentInstances, setRecentInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAllInstances, setShowAllInstances] = useState(false);
  const [displayCount, setDisplayCount] = useState(5);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const instances = await instanceService.getInstances();
      
      // 통계 계산
      const newStats = {
        total: instances.length,
        running: instances.filter(i => i.state === 'running').length,
        stopped: instances.filter(i => i.state === 'stopped').length,
        pending: instances.filter(i => i.state === 'pending').length,
        other: instances.filter(i => !['running', 'stopped', 'pending'].includes(i.state)).length
      };
      
      setStats(newStats);
      
      // 인스턴스 정렬: 1순위 - 상태 변경 시간, 2순위 - 시작 시간
      const sortedInstances = instances
        .sort((a, b) => {
          // 1순위: 상태 변경 시간 (최신순)
          const aStateTime = new Date(a.stateTransitionTime || a.launchTime);
          const bStateTime = new Date(b.stateTransitionTime || b.launchTime);
          
          if (aStateTime.getTime() !== bStateTime.getTime()) {
            return bStateTime - aStateTime;
          }
          
          // 2순위: 시작 시간 (최신순)
          return new Date(b.launchTime) - new Date(a.launchTime);
        });
      
      setRecentInstances(sortedInstances);
      setError('');
    } catch (error) {
      console.error('대시보드 데이터 로드 오류:', error);
      setError('대시보드 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (state) => {
    switch (state) {
      case 'running': return 'text-green-600 bg-green-100';
      case 'stopped': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'stopping': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">대시보드 로딩 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">오류</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={loadDashboardData}
              className="mt-2 text-sm text-red-600 underline hover:text-red-500"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 페이지 헤더 */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">대시보드</h1>
          <p className="mt-1 text-sm text-gray-600">
            AWS EC2 인스턴스 현황을 한눈에 확인하세요
          </p>
        </div>
        
        {/* 사용자 프로필 카드 */}
        {user && (
          <div className="bg-white shadow rounded-lg p-4 min-w-64">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.name || user.username || user.email || '사용자'}
                </p>
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <span>{user.email}</span>
                </div>
                {user.organization && (
                  <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                    <Building2 className="h-3 w-3" />
                    <span>{user.organization}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 통계 카드들 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Server className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    전체 인스턴스
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.total}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    실행 중
                  </dt>
                  <dd className="text-lg font-medium text-green-600">
                    {stats.running}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    중지됨
                  </dt>
                  <dd className="text-lg font-medium text-red-600">
                    {stats.stopped}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    처리 중
                  </dt>
                  <dd className="text-lg font-medium text-yellow-600">
                    {stats.pending}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 최근 인스턴스 목록 */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            최근 활동 인스턴스
          </h3>
          
          {recentInstances.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              표시할 인스턴스가 없습니다.
            </p>
          ) : (
            <div>
              <div className="space-y-3">
                {(showAllInstances ? recentInstances : recentInstances.slice(0, displayCount))
                  .map((instance) => (
                  <div
                    key={instance.instanceId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <p className="text-sm font-medium text-gray-900">
                          {instance.instanceId}
                        </p>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(instance.state)}`}>
                          {instance.state}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                        <span>{instance.instanceType}</span>
                        <span>{instance.availabilityZone}</span>
                        <span>{formatDate(instance.launchTime)}</span>
                        {instance.stateTransitionTime && instance.stateTransitionTime !== instance.launchTime && (
                          <span className="text-blue-600">
                            상태변경: {formatDate(instance.stateTransitionTime)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0">
                      {instance.tags?.Name && (
                        <p className="text-sm text-gray-900 font-medium">
                          {instance.tags.Name}
                        </p>
                      )}
                      {instance.tags?.Environment && (
                        <p className="text-xs text-gray-500">
                          {instance.tags.Environment}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* 모두 보기/접기 버튼 */}
              {recentInstances.length > displayCount && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => setShowAllInstances(!showAllInstances)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    {showAllInstances 
                      ? `처음 ${displayCount}개만 보기` 
                      : `모든 인스턴스 보기 (${recentInstances.length}개)`
                    }
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 새로고침 버튼 */}
      <div className="flex justify-center">
        <button
          onClick={loadDashboardData}
          className="btn-primary"
        >
          새로고침
        </button>
      </div>
    </div>
  );
};

export default Dashboard;